<template>
  <div class="sovi-esign-page sovi-home">
    <router-link to="/" class="sovi-esign-page__logo">
      <sovi-logo :size="48" />
    </router-link>

    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn
          class="language-menu"
          size="default"
          color="#eca1c2"
          v-bind="props"
        >
          <template #prepend>
            <v-icon icon="mdi-web" />
          </template>

          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="option in $i18n.availableLocales"
          :key="option"
          class="align-items-center"
          @click="onLocaleChange(option)"
        >
          <v-row align="center" no-gutters>
            {{ option.toUpperCase() }}
          </v-row>
        </v-list-item>
      </v-list>
    </v-menu>

    <sovi-content-nav />

    <!-- Hero / intro section with parallax -->
    <sovi-home-card
      background="/images/sahkoinen-allekirjoitus-1.webp"
      src="/images/sahkoinen-allekirjoitus-2.webp"
      :title="t('heroTitle')"
      fixed
      style="padding-top: 100px"
    >
      <strong>{{ t('heroSubtitle') }}</strong>

      <p>{{ t('heroText1') }}</p>

      <p>{{ t('heroText2') }}</p>

      <p>{{ t('heroText3') }}</p>
    </sovi-home-card>

    <!-- What is e-signature -->
    <sovi-home-card
      src="/images/sahkoinen-allekirjoitus-3.webp"
      :title="t('whatIsTitle')"
      flipped
    >
      <p>{{ t('whatIsText') }}</p>

      <p>{{ t('whatIsProcess') }}</p>

      <ol class="ms-6 list-decimal">
        <li>{{ t('whatIsStep1') }}</li>

        <li>{{ t('whatIsStep2') }}</li>

        <li>{{ t('whatIsStep3') }}</li>

        <li>{{ t('whatIsStep4') }}</li>

        <li>{{ t('whatIsStep5') }}</li>
      </ol>

      <p>{{ t('whatIsConclusion') }}</p>
    </sovi-home-card>

    <!-- Why companies switch -->
    <sovi-home-card
      src="/images/sahkoinen-allekirjoitus-4.webp"
      :title="t('whySwitchTitle')"
    >
      <p>{{ t('whySwitchText1') }}</p>

      <p>{{ t('whySwitchText2') }}</p>

      <p>{{ t('whySwitchText3') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('whySwitchBenefit1') }}</li>

        <li>{{ t('whySwitchBenefit2') }}</li>

        <li>{{ t('whySwitchBenefit3') }}</li>

        <li>{{ t('whySwitchBenefit4') }}</li>

        <li>{{ t('whySwitchBenefit5') }}</li>

        <li>{{ t('whySwitchBenefit6') }}</li>
      </ul>
    </sovi-home-card>

    <!-- Key benefits section -->
    <sovi-home-card
      src="/images/content-7.webp"
      :title="t('benefitsTitle')"
      flipped
    >
      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('benefit1Title') }}
      </h3>

      <p>{{ t('benefit1Text1') }}</p>

      <p>{{ t('benefit1Text2') }}</p>

      <p>{{ t('benefit1Text3') }}</p>

      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('benefit2Title') }}
      </h3>

      <p>{{ t('benefit2Text') }}</p>

      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('benefit3Title') }}
      </h3>

      <p>{{ t('benefit3Text') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('benefit3Item1') }}</li>

        <li>{{ t('benefit3Item2') }}</li>

        <li>{{ t('benefit3Item3') }}</li>

        <li>{{ t('benefit3Item4') }}</li>
      </ul>

      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('benefit4Title') }}
      </h3>

      <p>{{ t('benefit4Text') }}</p>

      <h3 class="home-title text-[clamp(20px,2.5vw,28px)] font-bold">
        {{ t('benefit5Title') }}
      </h3>

      <p>{{ t('benefit5Text') }}</p>
    </sovi-home-card>

    <!-- CTA section -->
    <sovi-home-card src="/images/content-8.webp" :title="t('ctaTitle')">
      <p>{{ t('ctaIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('ctaFeature1') }}</li>

        <li>{{ t('ctaFeature2') }}</li>

        <li>{{ t('ctaFeature3') }}</li>

        <li>{{ t('ctaFeature4') }}</li>

        <li>{{ t('ctaFeature5') }}</li>

        <li>{{ t('ctaFeature6') }}</li>
      </ul>

      <p>
        <strong>{{ t('ctaAction') }}</strong>
      </p>
    </sovi-home-card>

    <!-- Navigation links -->
    <div
      style="
        padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);
        background-color: #fff;
      "
    >
      <div style="display: grid; gap: 8px">
        <router-link to="/sahkoinen-allekirjoitus-yrityksille">
          {{ t('navBusiness') }} →
        </router-link>

        <router-link to="/sahkoinen-allekirjoitus-turvallisuus">
          {{ t('navSecurity') }} →
        </router-link>

        <router-link to="/ukk">{{ t('navFaq') }} →</router-link>
      </div>
    </div>

    <div class="sovi-esign-page__login">
      <sovi-home-login />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { definePage } from 'vue-router/auto';
import { useRouteMeta } from '@/composables/useRouteMeta';
import { useAuthorizationStore } from '@/stores/authorization';
import { loadRecaptcha } from '@/plugins/recaptcha';
import SoviHomeLogin from '@/components/home/SoviHomeLogin.vue';

definePage({
  meta: {
    hero: false,
    restricted: false,
    maxContainer: true,
  },
});

useRouteMeta({
  title:
    'Sähköinen allekirjoitus – Turvallinen ja helppo sähköinen allekirjoitus yrityksille | Sovi.fi',
  description:
    'Allekirjoita sopimukset sähköisesti nopeasti ja turvallisesti. Sovi.fi tarjoaa kotimaisen sähköisen allekirjoituspalvelun yrityksille ilman kuukausimaksuja – maksa vain käytöstä.',
});

const { locale } = useI18n({ useScope: 'global' });
const { t } = useI18n();
const theme = useTheme();
const { isAuthorized } = useAuthorizationStore();

const onLocaleChange = (newLocale: string) => {
  locale.value = newLocale;
  localStorage.setItem('sovi-locale', newLocale);
};

onMounted(() => {
  document.getElementById('sovi-main')?.scrollTo(0, 0);
  window.scrollTo(0, 0);
  loadRecaptcha();

  const storedLocale = localStorage.getItem('sovi-locale');

  if (storedLocale && storedLocale !== locale.value) {
    locale.value = storedLocale;
  }

  theme.global.name.value = 'light';

  isAuthorized();
});
</script>

<style lang="scss">
.grecaptcha-badge {
  visibility: hidden;
}

.sovi-esign-page {
  position: relative;
  background-color: #fff;
  font-size: clamp(18px, 2vw, 22px);

  &__logo {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }

  .language-menu {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    box-shadow:
      rgba(0, 0, 0, 0.15) 0px 15px 25px,
      rgba(0, 0, 0, 0.05) 0px 5px 10px;
  }

  .v-table {
    max-width: 100%;
    overflow-x: auto;
    display: block;
    font-size: clamp(12px, 1.5vw, 16px);
  }

  .home-title {
    line-height: 1.3;

    @media (min-width: 768px) {
      line-height: 1.1;
    }
    --title-color: rgb(var(--v-theme-primary));

    &.alt {
      --title-color: #eca1c2;
    }

    &::first-letter {
      padding-bottom: 2px;
      box-shadow: inset 0 calc(-1 * clamp(4px, 0.5vw, 6px)) 0 0
        var(--title-color);
    }
  }

  &__login {
    overflow: hidden;

    .sovi-home__login {
      &::before,
      &::after {
        display: none;
      }
    }
    background-color: #fff;
    padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);

    .sovi-home__login-form {
      display: grid;
      gap: clamp(16px, 3vw, 32px);
      position: relative;
      z-index: 1;

      h1 {
        font-size: clamp(24px, 3vw, 32px);
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "fi": {
    "heroTitle": "Sähköinen allekirjoitus",
    "heroSubtitle": "Allekirjoita asiakirjat sähköisesti – nopeasti, turvallisesti ja juridisesti sitovasti",
    "heroText1": "Sähköinen allekirjoitus on muuttanut tapaa, jolla yritykset, organisaatiot ja yrittäjät käsittelevät sopimuksia. Paperisten asiakirjojen tulostaminen, postittaminen, skannaaminen ja arkistointi vievät aikaa, hidastavat päätöksentekoa ja aiheuttavat turhia kustannuksia. Kun allekirjoitus tapahtuu sähköisesti, koko prosessi voidaan hoitaa muutamassa minuutissa riippumatta siitä, missä allekirjoittajat sijaitsevat.",
    "heroText2": "Sovi.fi on suomalainen sähköinen allekirjoitus- ja asiakirjahallintapalvelu, jonka avulla voit lähettää asiakirjoja allekirjoitettavaksi turvallisesti, seurata niiden etenemistä reaaliaikaisesti ja säilyttää valmiit dokumentit yhdessä paikassa. Palvelu sopii pienille yrityksille, kasvuyrityksille ja suuremmille organisaatioille.",
    "heroText3": "Sovi.fi:n hinnoittelun lähtökohtana on joustavuus. Maksat vain silloin, kun käytät palvelua. Tämä tekee sähköisestä allekirjoituksesta kustannustehokkaan vaihtoehdon myös yrityksille, jotka allekirjoittavat asiakirjoja vain satunnaisesti.",
    "whatIsTitle": "Mitä sähköinen allekirjoitus tarkoittaa?",
    "whatIsText": "Sähköinen allekirjoitus tarkoittaa asiakirjan hyväksymistä digitaalisesti siten, että allekirjoittajan henkilöllisyys voidaan todentaa ja allekirjoituksen tapahtumat voidaan jälkikäteen osoittaa luotettavasti. Samalla asiakirjan eheys säilyy, eikä sitä voida muuttaa huomaamatta allekirjoituksen jälkeen.",
    "whatIsProcess": "Tyypillinen allekirjoitusprosessi etenee näin:",
    "whatIsStep1": "Asiakirja luodaan tai ladataan palveluun.",
    "whatIsStep2": "Vastaanottajat lisätään.",
    "whatIsStep3": "Vastaanottajille lähetetään allekirjoituspyyntö.",
    "whatIsStep4": "Vastaanottajat tunnistautuvat ja allekirjoittavat asiakirjan.",
    "whatIsStep5": "Valmis asiakirja tallentuu turvallisesti kaikkien osapuolten saataville.",
    "whatIsConclusion": "Koko prosessi voidaan toteuttaa ilman tulostamista, skannaamista tai fyysisiä tapaamisia.",
    "whySwitchTitle": "Miksi yritykset siirtyvät sähköiseen allekirjoitukseen?",
    "whySwitchText1": "Digitalisaation myötä yritykset haluavat vähentää manuaalista työtä ja nopeuttaa liiketoimintaa. Sopimusten odottaminen on yksi yleisimmistä syistä siihen, että kaupat viivästyvät tai projektit eivät käynnisty ajallaan.",
    "whySwitchText2": "Sähköinen allekirjoitus ratkaisee tämän ongelman.",
    "whySwitchText3": "Kun sopimus voidaan allekirjoittaa heti sähköpostista tai mobiililaitteella, päätöksenteko nopeutuu huomattavasti. Samalla vähenevät myös tulostamiseen, postittamiseen ja asiakirjojen arkistointiin liittyvät kustannukset.",
    "whySwitchBenefit1": "nopeampia kauppoja",
    "whySwitchBenefit2": "tehokkaampaa henkilöstöhallintoa",
    "whySwitchBenefit3": "sujuvampaa asiakaskokemusta",
    "whySwitchBenefit4": "parempaa tietoturvaa",
    "whySwitchBenefit5": "vähemmän paperityötä",
    "whySwitchBenefit6": "helpompaa asiakirjojen hallintaa",
    "benefitsTitle": "Sähköisen allekirjoituksen tärkeimmät hyödyt",
    "benefit1Title": "1. Merkittävä ajansäästö",
    "benefit1Text1": "Perinteinen allekirjoitusprosessi voi kestää päiviä tai jopa viikkoja. Asiakirja tulostetaan, allekirjoitetaan, skannataan ja lähetetään takaisin. Jokainen vaihe lisää viivettä.",
    "benefit1Text2": "Sähköisen allekirjoituksen avulla sama prosessi voidaan usein hoitaa muutamassa minuutissa.",
    "benefit1Text3": "Nopeampi allekirjoitus tarkoittaa myös nopeampaa laskutusta, projektien käynnistymistä ja päätöksentekoa.",
    "benefit2Title": "2. Parempi asiakaskokemus",
    "benefit2Text": "Asiakkaat odottavat nykyään, että palvelut toimivat digitaalisesti. Harva haluaa enää tulostaa sopimuksia tai käydä toimistolla pelkästään allekirjoittamassa asiakirjan. Kun allekirjoitus onnistuu tietokoneella, tabletilla tai puhelimella, asiakkaan kokemus paranee merkittävästi.",
    "benefit3Title": "3. Turvallisuus ja jäljitettävyys",
    "benefit3Text": "Sähköinen allekirjoitus tarjoaa paremman jäljitettävyyden kuin paperinen allekirjoitus. Jokaisesta allekirjoituksesta jää tapahtumaloki, jonka avulla voidaan myöhemmin todentaa esimerkiksi:",
    "benefit3Item1": "kuka allekirjoitti",
    "benefit3Item2": "milloin allekirjoitus tehtiin",
    "benefit3Item3": "missä vaiheessa prosessi eteni",
    "benefit3Item4": "onko asiakirjaa muutettu allekirjoituksen jälkeen",
    "benefit4Title": "4. Kustannussäästöt",
    "benefit4Text": "Paperi, kirjekuoret, tulostimet, postitus ja fyysinen arkistointi aiheuttavat yrityksille yllättävän paljon kustannuksia. Kun allekirjoitus tapahtuu sähköisesti, nämä kulut vähenevät merkittävästi. Samalla työntekijöiden aikaa vapautuu tuottavampiin tehtäviin.",
    "benefit5Title": "5. Ympäristöystävällisyys",
    "benefit5Text": "Sähköinen allekirjoitus vähentää paperinkulutusta, kuljetuksia ja fyysisen arkistoinnin tarvetta. Yritys voi samalla pienentää ympäristövaikutuksiaan ja edistää vastuullisuustavoitteitaan.",
    "ctaTitle": "Aloita sähköinen allekirjoitus Sovi.fi:ssä",
    "ctaIntro": "Olipa kyse yksittäisestä sopimuksesta tai tuhansista asiakirjoista vuodessa, Sovi.fi tarjoaa helpon ja turvallisen ratkaisun sähköiseen allekirjoittamiseen. Sovi.fi:n avulla voit:",
    "ctaFeature1": "lähettää asiakirjoja allekirjoitettavaksi muutamassa minuutissa",
    "ctaFeature2": "seurata allekirjoitusprosessin etenemistä reaaliaikaisesti",
    "ctaFeature3": "käyttää valmiita asiakirjamalleja",
    "ctaFeature4": "hallita dokumentteja kansioissa",
    "ctaFeature5": "vähentää paperityötä ja manuaalisia työvaiheita",
    "ctaFeature6": "nopeuttaa sopimusprosesseja koko organisaatiossa",
    "ctaAction": "Luo maksuton tili ja lähetä ensimmäinen asiakirjasi allekirjoitettavaksi muutamassa minuutissa.",
    "navBusiness": "Sähköinen allekirjoitus yrityksille",
    "navSecurity": "Turvallisuus ja juridinen pätevyys",
    "navFaq": "Usein kysytyt kysymykset"
  },
  "en": {
    "heroTitle": "Electronic Signature",
    "heroSubtitle": "Sign documents electronically — fast, secure, and legally binding",
    "heroText1": "Electronic signatures have transformed how businesses, organizations, and entrepreneurs handle contracts. Printing, mailing, scanning, and archiving paper documents takes time, slows down decision-making, and creates unnecessary costs. With e-signatures, the entire process can be completed in minutes — no matter where the signers are located.",
    "heroText2": "Sovi.fi is a Finnish electronic signature and document management service that lets you send documents for signing securely, track their progress in real time, and store completed documents in one place. The service is designed for small businesses, growing companies, and larger organizations alike.",
    "heroText3": "Sovi.fi's pricing is built around flexibility. You only pay when you use the service. This makes electronic signatures a cost-effective option even for businesses that only sign documents occasionally.",
    "whatIsTitle": "What is an electronic signature?",
    "whatIsText": "An electronic signature means approving a document digitally in a way that verifies the signer's identity and allows the signing events to be reliably demonstrated after the fact. At the same time, the document's integrity is preserved and cannot be altered undetected after signing.",
    "whatIsProcess": "A typical signing process works like this:",
    "whatIsStep1": "The document is created or uploaded to the service.",
    "whatIsStep2": "Recipients are added.",
    "whatIsStep3": "A signing request is sent to the recipients.",
    "whatIsStep4": "Recipients verify their identity and sign the document.",
    "whatIsStep5": "The completed document is securely stored and accessible to all parties.",
    "whatIsConclusion": "The entire process can be completed without printing, scanning, or in-person meetings.",
    "whySwitchTitle": "Why are businesses switching to electronic signatures?",
    "whySwitchText1": "As digitalization progresses, businesses want to reduce manual work and speed up operations. Waiting for contracts is one of the most common reasons deals are delayed or projects fail to start on time.",
    "whySwitchText2": "Electronic signatures solve this problem.",
    "whySwitchText3": "When a contract can be signed immediately from email or on a mobile device, decision-making speeds up significantly. At the same time, costs related to printing, mailing, and document archiving are reduced.",
    "whySwitchBenefit1": "faster deals",
    "whySwitchBenefit2": "more efficient HR management",
    "whySwitchBenefit3": "smoother customer experience",
    "whySwitchBenefit4": "better data security",
    "whySwitchBenefit5": "less paperwork",
    "whySwitchBenefit6": "easier document management",
    "benefitsTitle": "Key benefits of electronic signatures",
    "benefit1Title": "1. Significant time savings",
    "benefit1Text1": "A traditional signing process can take days or even weeks. The document is printed, signed, scanned, and sent back. Each step adds delay.",
    "benefit1Text2": "With electronic signatures, the same process can often be completed in just a few minutes.",
    "benefit1Text3": "Faster signing also means faster invoicing, project kickoffs, and decision-making.",
    "benefit2Title": "2. Better customer experience",
    "benefit2Text": "Customers today expect services to work digitally. Few want to print contracts or visit an office just to sign a document. When signing is possible on a computer, tablet, or phone, the customer experience improves significantly.",
    "benefit3Title": "3. Security and traceability",
    "benefit3Text": "Electronic signatures offer better traceability than paper signatures. Every signature produces an event log that can later verify, for example:",
    "benefit3Item1": "who signed",
    "benefit3Item2": "when the signature was made",
    "benefit3Item3": "what stage the process reached",
    "benefit3Item4": "whether the document was modified after signing",
    "benefit4Title": "4. Cost savings",
    "benefit4Text": "Paper, envelopes, printers, postage, and physical archiving create surprisingly high costs for businesses. When signing happens electronically, these expenses are significantly reduced. At the same time, employees' time is freed up for more productive tasks.",
    "benefit5Title": "5. Environmental friendliness",
    "benefit5Text": "Electronic signatures reduce paper consumption, transportation, and the need for physical archiving. Businesses can simultaneously reduce their environmental impact and advance their sustainability goals.",
    "ctaTitle": "Start signing electronically with Sovi.fi",
    "ctaIntro": "Whether it's a single contract or thousands of documents per year, Sovi.fi offers an easy and secure solution for electronic signing. With Sovi.fi you can:",
    "ctaFeature1": "send documents for signing in minutes",
    "ctaFeature2": "track signing progress in real time",
    "ctaFeature3": "use ready-made document templates",
    "ctaFeature4": "manage documents in folders",
    "ctaFeature5": "reduce paperwork and manual steps",
    "ctaFeature6": "speed up contracting processes across the organization",
    "ctaAction": "Create a free account and send your first document for signing in just a few minutes.",
    "navBusiness": "Electronic signatures for businesses",
    "navSecurity": "Security and legal validity",
    "navFaq": "Frequently asked questions"
  }
}
</i18n>
